import { ChatControllerBindings, OpenedPhotos } from '@/components/chat/chat-controller.types';
import { useModalAnswerPickedTemplates } from '@/components/modal-answer-picked/use-modal-answer-picked-templates';
import { ChatMessage } from '@/contracts/marketplace/chat-message';
import { useAuth } from '@/context/auth.context';
import { useChat } from '@/context/chat.context';
import { useClientFeatureControls } from '@/context/client-feature-controls.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useReviewAnswerJobs } from '@/context/review-answer-jobs.context';
import { useReviewsPageQueryContext } from '@/context/reviews-page.context';
import { useSelectedAutoRepliesModal } from '@/context/selected-auto-replies-modal.context';
import { useRefreshClientAiQuota } from '@/hooks/use-refresh-client-ai-quota';
import {
    isReviewAiAnswerJobCanceledError,
    useCancelableReviewAiAnswerJob,
} from '@/hooks/use-cancelable-review-ai-answer-job';
import { ReviewPhotoEntity } from '@/entities/review.entity';
import { Marketplace } from '@/enums/marketplace';
import { QUEUE_JOBS } from '@/constants/queue';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SetStateAction } from 'react';
import { createClientLogger } from '@/client/logging/client-logger';
import { logOfferNotAcceptedMarketplacePolicyWarning } from '@/client/logging/offer-not-accepted-policy-logger';
import { isClientAiQuotaExhaustedAvailability } from '@/shared/client-ai-quota';
import { formatMarketplaceApiErrorMessageFromUnknown } from '@/shared/errors/marketplace-api-error-message';
import {
    createAiAnswerDraftSource,
    createAnswerDraftSourceFromAnswerKind,
    createHumanAnswerDraftSource,
    createTemplateAnswerDraftSource,
    resolveAnswerKindForSubmit,
    type AnswerDraftSource,
} from '@/shared/answer-draft-source';
import { resolveOperationId } from '@/shared/operation-id';

type UseChatControllerOptions = {
    onMessageSubmitted?: () => void;
};

type ChatReplyTarget = Pick<ChatMessage, 'reviewFeedItemId' | 'reviewCommentId'>;

const CHAT_AI_FEATURE_CONTROL_ACTION = 'Включите «Разрешить формировать ИИ-ответы» в Настройках.';
const CHAT_SEND_FEATURE_DISABLED_REASON =
    'Отправка ответов отключена в настройках клиента. Включите «Разрешить отправку ответов» в Настройках.';
const CHAT_SEND_EMPTY_REASON = 'Введите текст ответа или нажмите «ИИ-ответ», чтобы подготовить сообщение.';
const CHAT_AI_NO_TARGET_REASON =
    'ИИ-ответ недоступен: не выбран отзыв или сообщение без ответа. Выберите доступное сообщение в диалоге.';
const CHAT_SEND_NO_TARGET_REASON =
    'Отправка недоступна: не выбран отзыв или сообщение без ответа. Выберите доступное сообщение в диалоге.';
const CHAT_ACTIVE_JOB_REASON =
    'Ответ уже отправляется. Дождитесь завершения текущей отправки или отмените активную задачу.';
const CHAT_THREAD_LOCKED_REASON =
    'В этом диалоге уже есть официальный ответ, который нельзя редактировать. Отправка нового ответа недоступна.';
const CHAT_ALREADY_ANSWERED_REASON =
    'На этот отзыв уже есть ответ. Откройте правку, если ответ доступен для редактирования.';
const CHAT_AI_GENERATING_REASON = 'ИИ-ответ уже формируется. Дождитесь завершения генерации.';
const chatControllerLogger = createClientLogger({
    scope: 'chat-controller',
});

const appendActionSentence = (reason: string, action: string): string => {
    const normalizedReason = reason.trim();
    if (!normalizedReason) {
        return action;
    }

    if (normalizedReason.includes(action)) {
        return normalizedReason;
    }

    const separator = /[.!?]$/.test(normalizedReason) ? ' ' : '. ';
    return `${normalizedReason}${separator}${action}`;
};

const toReplyTargetKey = (target: ChatReplyTarget): string =>
    `${target.reviewFeedItemId}::${target.reviewCommentId ?? ''}`;

const toReplyTargetFromMessage = (message: ChatMessage): ChatReplyTarget => ({
    reviewFeedItemId: message.reviewFeedItemId,
    ...(message.reviewCommentId ? { reviewCommentId: message.reviewCommentId } : {}),
});

export const useChatController = ({ onMessageSubmitted }: UseChatControllerOptions = {}): ChatControllerBindings => {
    const [messageText, setMessageTextState] = useState<string>('');
    const [editedMessageText, setEditedMessageText] = useState<string>('');
    const [isManualEditOpened, setIsManualEditOpened] = useState<boolean>(false);
    const [aiErrorText, setAiErrorText] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isAiGenerating, setIsAiGenerating] = useState<boolean>(false);
    const [isAiAnswerGenerated, setIsAiAnswerGenerated] = useState<boolean>(false);
    const [isAiDraftReadyForApproval, setIsAiDraftReadyForApproval] = useState<boolean>(false);
    const [answerDraftSource, setAnswerDraftSource] = useState<AnswerDraftSource>(() => createHumanAnswerDraftSource());
    const [openedPhotos, setOpenedPhotos] = useState<OpenedPhotos | null>(null);
    const [isPhotoLoading, setIsPhotoLoading] = useState<boolean>(false);
    const [selectedAnswerTarget, setSelectedAnswerTarget] = useState<ChatReplyTarget | null>(null);
    const latestMessageTextRef = useRef(messageText);
    const appliedPendingAnswerDraftIdRef = useRef<string | null>(null);

    const { accessToken } = useAuth();
    const { featureAvailability, featureControls } = useClientFeatureControls();
    const { reviewsApi } = useMarketplaceApi();
    const { openSelectedAutoRepliesModal } = useSelectedAutoRepliesModal();
    const { refetchReviewsAndProducts } = useReviewsPageQueryContext();
    const { review, history, setHistory, pendingAnswerDraft, setPendingAnswerDraft } = useChat();
    const { getAnswerJob, jobs, refetchActiveJobs } = useReviewAnswerJobs();
    const refreshClientAiQuota = useRefreshClientAiQuota();
    const aiAnswerJob = useCancelableReviewAiAnswerJob({
        accessToken,
        jobName: QUEUE_JOBS.generateReviewAiAnswer,
    });

    useEffect(() => {
        latestMessageTextRef.current = messageText;
    }, [messageText]);

    const applyMessageText = useCallback((nextValue: string) => {
        latestMessageTextRef.current = nextValue;
        setMessageTextState(nextValue);
    }, []);

    const latestAnswerByFeedItemId = useMemo(() => {
        const latestAnswers = new Map<string, ChatMessage>();
        for (const message of history) {
            if (message.isAnswer) {
                latestAnswers.set(message.reviewFeedItemId, message);
            }
        }

        return latestAnswers;
    }, [history]);

    const persistedThreadAnswers = useMemo(
        () => history.filter((message) => message.isAnswer && !message.inQueue),
        [history],
    );

    const hasPersistedWbThreadAnswer = useMemo(
        () => review.platform === Marketplace.Wb && persistedThreadAnswers.length > 0,
        [persistedThreadAnswers, review.platform],
    );

    const canEditSingleWbThreadAnswer = useMemo(() => {
        if (review.platform !== Marketplace.Wb) {
            return false;
        }

        if (persistedThreadAnswers.length !== 1) {
            return false;
        }

        return Boolean(persistedThreadAnswers[0]?.editable);
    }, [persistedThreadAnswers, review.platform]);

    const isThreadEditingLocked = useMemo(
        () => review.platform === Marketplace.Wb && persistedThreadAnswers.length > 0 && !canEditSingleWbThreadAnswer,
        [canEditSingleWbThreadAnswer, persistedThreadAnswers, review.platform],
    );

    const activeAnswerJobFeedItemIds = useMemo(() => {
        const jobReviewIds = new Set<string>();
        for (const job of jobs) {
            const jobReviewId = job.data.reviewFeedItemId?.trim();
            if (jobReviewId) {
                jobReviewIds.add(jobReviewId);
            }
        }

        return jobReviewIds;
    }, [jobs]);

    const isAnswerTargetSelectable = useCallback(
        (message: ChatMessage): boolean => {
            if (message.isAnswer) {
                return false;
            }

            if (activeAnswerJobFeedItemIds.has(message.reviewFeedItemId)) {
                return false;
            }

            const latestTargetAnswer = latestAnswerByFeedItemId.get(message.reviewFeedItemId);
            if (!latestTargetAnswer) {
                if (hasPersistedWbThreadAnswer) {
                    return false;
                }

                return true;
            }

            if (latestTargetAnswer.inQueue) {
                return false;
            }

            if (review.platform === Marketplace.Ozon) {
                return true;
            }

            if (review.platform === Marketplace.Wb) {
                if (!canEditSingleWbThreadAnswer) {
                    return false;
                }

                return Boolean(latestTargetAnswer.editable);
            }

            return Boolean(latestTargetAnswer.editable);
        },
        [
            activeAnswerJobFeedItemIds,
            canEditSingleWbThreadAnswer,
            hasPersistedWbThreadAnswer,
            latestAnswerByFeedItemId,
            review.platform,
        ],
    );

    const answerTargets = useMemo(
        () => history.filter((message) => isAnswerTargetSelectable(message)),
        [history, isAnswerTargetSelectable],
    );

    useEffect(() => {
        setSelectedAnswerTarget({ reviewFeedItemId: review.id });
    }, [review.id]);

    const answerTargetMessage = useMemo(() => {
        if (!answerTargets.length) {
            return null;
        }

        if (selectedAnswerTarget) {
            const selectedTargetKey = toReplyTargetKey(selectedAnswerTarget);
            const exactTarget = answerTargets.find((message) => toReplyTargetKey(message) === selectedTargetKey);
            if (exactTarget) {
                return exactTarget;
            }

            const sameReviewTarget = answerTargets.find(
                (message) => message.reviewFeedItemId === selectedAnswerTarget.reviewFeedItemId,
            );
            if (sameReviewTarget) {
                return sameReviewTarget;
            }
        }

        const openedReviewTarget = answerTargets.find((message) => message.reviewFeedItemId === review.id);
        if (openedReviewTarget) {
            return openedReviewTarget;
        }

        return answerTargets[answerTargets.length - 1] || null;
    }, [answerTargets, selectedAnswerTarget, review.id]);

    const answerTargetKey = useMemo(
        () => (answerTargetMessage ? toReplyTargetKey(answerTargetMessage) : null),
        [answerTargetMessage],
    );
    const answerTargetReviewFeedItemId = answerTargetMessage?.reviewFeedItemId || null;
    const aiAnswerAvailability = featureAvailability.aiAnswerGeneration;
    const pendingApprovalDraft =
        pendingAnswerDraft?.status === 'pending_approval' &&
        pendingAnswerDraft.reviewFeedItemId === answerTargetReviewFeedItemId
            ? pendingAnswerDraft
            : null;
    const hasPendingApprovalDraft = !!pendingApprovalDraft;

    useEffect(() => {
        const pendingDraft = pendingAnswerDraft?.status === 'pending_approval' ? pendingAnswerDraft : null;
        if (!pendingDraft) {
            appliedPendingAnswerDraftIdRef.current = null;
            return;
        }

        if (appliedPendingAnswerDraftIdRef.current === pendingDraft.id) {
            return;
        }

        appliedPendingAnswerDraftIdRef.current = pendingDraft.id;
        setSelectedAnswerTarget({ reviewFeedItemId: pendingDraft.reviewFeedItemId });
        applyMessageText(pendingDraft.text);
        setEditedMessageText('');
        setAiErrorText(null);
        setIsEditing(false);
        setIsManualEditOpened(false);
        setIsAiAnswerGenerated(pendingDraft.answerKind === 'ai_unchanged' || pendingDraft.answerKind === 'ai_changed');
        setIsAiDraftReadyForApproval(true);
        setAnswerDraftSource(
            createAnswerDraftSourceFromAnswerKind({
                answerKind: pendingDraft.answerKind,
                originalText: pendingDraft.text,
            }),
        );
    }, [applyMessageText, pendingAnswerDraft]);

    useEffect(() => {
        if (!isThreadEditingLocked) {
            return;
        }

        applyMessageText('');
        setEditedMessageText('');
        setAiErrorText(null);
        setIsAiAnswerGenerated(false);
        setIsAiDraftReadyForApproval(false);
        setAnswerDraftSource(createHumanAnswerDraftSource());
        setIsManualEditOpened(false);
        setIsEditing(false);
    }, [applyMessageText, isThreadEditingLocked]);

    const onAnswerTargetSelect = useCallback(
        (message: ChatMessage) => {
            if (!isAnswerTargetSelectable(message)) return;

            const nextTarget = toReplyTargetFromMessage(message);
            const nextTargetKey = toReplyTargetKey(nextTarget);
            if (answerTargetKey !== nextTargetKey) {
                void aiAnswerJob.cancelActive('target_changed');
                setEditedMessageText('');
                setAiErrorText(null);
                setIsAiAnswerGenerated(false);
                setIsAiDraftReadyForApproval(false);
                setAnswerDraftSource(createHumanAnswerDraftSource());
                setIsManualEditOpened(false);
                setIsEditing(false);
            }
            setSelectedAnswerTarget(nextTarget);
        },
        [aiAnswerJob, answerTargetKey, isAnswerTargetSelectable],
    );

    const job = useMemo(() => {
        if (!answerTargetReviewFeedItemId) {
            return undefined;
        }
        return getAnswerJob(answerTargetReviewFeedItemId);
    }, [answerTargetReviewFeedItemId, getAnswerJob, jobs]);

    const hasAnswerJob = !!job;

    const targetHasAnswer = useMemo(() => {
        if (!answerTargetReviewFeedItemId) {
            return false;
        }
        return history.some((message) => message.isAnswer && message.reviewFeedItemId === answerTargetReviewFeedItemId);
    }, [answerTargetReviewFeedItemId, history]);

    const targetExistingAnswerText = useMemo(() => {
        if (!answerTargetReviewFeedItemId) {
            return '';
        }

        const targetAnswerMessage = history.findLast(
            (message) =>
                message.isAnswer && message.reviewFeedItemId === answerTargetReviewFeedItemId && !message.inQueue,
        );
        if (targetAnswerMessage?.text) {
            return targetAnswerMessage.text;
        }
        if (answerTargetReviewFeedItemId === review.id) {
            return review.answer?.text || '';
        }
        return '';
    }, [answerTargetReviewFeedItemId, history, review.id, review.answer]);

    const changeEditing = () => {
        if (isThreadEditingLocked) {
            return;
        }

        setIsEditing((prev) => {
            const nextValue = !prev;

            if (nextValue) {
                applyMessageText(targetExistingAnswerText);
            } else {
                applyMessageText('');
            }

            setEditedMessageText('');
            setIsManualEditOpened(false);
            setAiErrorText(null);
            setIsAiAnswerGenerated(false);
            setIsAiDraftReadyForApproval(false);
            setAnswerDraftSource(createHumanAnswerDraftSource());

            return nextValue;
        });
    };

    const refetchHistory = async (reviewFeedItemId: string) => {
        const r = await reviewsApi.getHistory(reviewFeedItemId, { authToken: accessToken });
        setHistory(r.data);

        refetchActiveJobs(accessToken);
    };

    const submitSendChatMessage = async () => {
        const reviewToAnswer = answerTargetMessage;
        if (!reviewToAnswer) return;
        if (!featureControls.allowResponseSending) return;

        const preparedText = editedMessageText.trim() || messageText.trim();
        if (!preparedText) return;

        const answerKind = resolveAnswerKindForSubmit({
            source: answerDraftSource,
            text: preparedText,
        });

        void aiAnswerJob.cancelActive('answer_submitted');
        setAiErrorText(null);
        let submitAction = 'chat.answer.create';
        if (isEditing) {
            submitAction = 'chat.answer.update';
        } else if (pendingApprovalDraft) {
            submitAction = 'chat.answer.draft_approve';
        }

        try {
            if (isEditing) {
                await reviewsApi.updateReviewAnswer(
                    {
                        reviewFeedItemId: reviewToAnswer.reviewFeedItemId,
                        text: preparedText,
                        answerKind,
                    },
                    {
                        authToken: accessToken,
                    },
                );
            } else if (pendingApprovalDraft) {
                await reviewsApi.approveAnswerDraft(
                    reviewToAnswer.reviewFeedItemId,
                    {
                        text: preparedText,
                        answerKind,
                    },
                    {
                        authToken: accessToken,
                    },
                );
                setPendingAnswerDraft(null);
            } else {
                await reviewsApi.createReviewAnswers(
                    [
                        {
                            reviewFeedItemId: reviewToAnswer.reviewFeedItemId,
                            text: preparedText,
                            answerKind,
                        },
                    ],
                    {
                        authToken: accessToken,
                    },
                );
            }

            try {
                await refetchHistory(reviewToAnswer.reviewFeedItemId);
            } finally {
                applyMessageText('');
                setEditedMessageText('');
                setAiErrorText(null);
                setIsAiAnswerGenerated(false);
                setIsAiDraftReadyForApproval(false);
                setAnswerDraftSource(createHumanAnswerDraftSource());
                setIsManualEditOpened(false);
                setIsEditing(false);
                onMessageSubmitted?.();
            }
        } catch (error) {
            logOfferNotAcceptedMarketplacePolicyWarning({
                logger: chatControllerLogger,
                message: '[client.chat.offer-policy] Chat answer action blocked before offer acceptance.',
                error,
                context: {
                    action: submitAction,
                    reviewFeedItemId: reviewToAnswer.reviewFeedItemId,
                },
            });
            setAiErrorText(formatMarketplaceApiErrorMessageFromUnknown(error) ?? 'Не удалось отправить ответ');
        }
    };

    const generateAiChatMessage = async () => {
        const reviewToAnswer = answerTargetMessage;
        if (!reviewToAnswer) return;
        if (!aiAnswerAvailability.enabled) {
            setAiErrorText(aiAnswerAvailability.reason);
            return;
        }

        setAiErrorText(null);
        setIsAiGenerating(true);
        const operationId = resolveOperationId(undefined, Date.now());

        try {
            const generatedAnswer = await aiAnswerJob.run({
                operationId,
                enqueue: (resolvedOperationId, { signal }) =>
                    reviewsApi.generateAiAnswer(
                        {
                            reviewFeedItemId: reviewToAnswer.reviewFeedItemId,
                            operationId: resolvedOperationId,
                        },
                        {
                            authToken: accessToken,
                            signal,
                        },
                    ),
            });

            void refetchReviewsAndProducts().catch(() => undefined);

            applyMessageText(generatedAnswer);
            setAiErrorText(null);
            setIsAiAnswerGenerated(true);
            setIsAiDraftReadyForApproval(true);
            setAnswerDraftSource(createAiAnswerDraftSource(generatedAnswer));
            await refreshClientAiQuota();
        } catch (error) {
            if (isReviewAiAnswerJobCanceledError(error)) {
                return;
            }

            applyMessageText('');
            logOfferNotAcceptedMarketplacePolicyWarning({
                logger: chatControllerLogger,
                message: '[client.chat.offer-policy] Chat AI answer action blocked before offer acceptance.',
                error,
                context: {
                    action: 'chat.ai_answer.generate',
                    operationId,
                    reviewFeedItemId: reviewToAnswer.reviewFeedItemId,
                },
            });
            setAiErrorText(
                formatMarketplaceApiErrorMessageFromUnknown(error) ??
                    'Ошибка сервиса ИИ. Не удалось сгенерировать ответ',
            );
            setIsAiAnswerGenerated(false);
            setIsAiDraftReadyForApproval(false);
            setAnswerDraftSource(createHumanAnswerDraftSource());
            await refreshClientAiQuota();
        } finally {
            setIsAiGenerating(false);
        }
    };

    const textAreaDisabled = useMemo(() => {
        if (!answerTargetMessage) return true;
        if (job) return true;
        if (isThreadEditingLocked) return true;
        return targetHasAnswer && !isEditing && !hasPendingApprovalDraft && review.platform !== Marketplace.Ozon;
    }, [
        answerTargetMessage,
        hasPendingApprovalDraft,
        isEditing,
        isThreadEditingLocked,
        job,
        review.platform,
        targetHasAnswer,
    ]);

    const submitDisabled = useMemo(() => {
        if (!answerTargetMessage) return true;
        if (!featureControls.allowResponseSending) return true;
        if (job) return true;
        if (isThreadEditingLocked) return true;
        return targetHasAnswer && !isEditing && !hasPendingApprovalDraft && review.platform !== Marketplace.Ozon;
    }, [
        answerTargetMessage,
        featureControls.allowResponseSending,
        hasPendingApprovalDraft,
        isEditing,
        isThreadEditingLocked,
        job,
        review.platform,
        targetHasAnswer,
    ]);

    const aiGenerateDisabled = useMemo(() => {
        if (!answerTargetMessage) return true;
        if (!aiAnswerAvailability.enabled) return true;
        if (job || isAiGenerating) return true;
        if (isThreadEditingLocked) return true;
        return targetHasAnswer && !isEditing && !hasPendingApprovalDraft && review.platform !== Marketplace.Ozon;
    }, [
        aiAnswerAvailability.enabled,
        answerTargetMessage,
        hasPendingApprovalDraft,
        targetHasAnswer,
        isEditing,
        isThreadEditingLocked,
        job,
        isAiGenerating,
        review.platform,
    ]);
    const isAiGenerateQuotaExhausted = isClientAiQuotaExhaustedAvailability(aiAnswerAvailability);

    const aiGenerateDisabledReason = useMemo(() => {
        if (!aiGenerateDisabled && !isAiGenerateQuotaExhausted) {
            return null;
        }

        if (!answerTargetMessage) {
            return CHAT_AI_NO_TARGET_REASON;
        }

        if (!aiAnswerAvailability.enabled) {
            const reason = aiAnswerAvailability.reason || 'Формирование ИИ-ответов отключено в настройках клиента';
            return aiAnswerAvailability.disabledBy === 'quota_exhausted'
                ? reason
                : appendActionSentence(reason, CHAT_AI_FEATURE_CONTROL_ACTION);
        }

        if (job) {
            return CHAT_ACTIVE_JOB_REASON;
        }

        if (isAiGenerating) {
            return CHAT_AI_GENERATING_REASON;
        }

        if (isThreadEditingLocked) {
            return CHAT_THREAD_LOCKED_REASON;
        }

        if (targetHasAnswer && !isEditing && !hasPendingApprovalDraft && review.platform !== Marketplace.Ozon) {
            return CHAT_ALREADY_ANSWERED_REASON;
        }

        return null;
    }, [
        aiAnswerAvailability.disabledBy,
        aiAnswerAvailability.enabled,
        aiAnswerAvailability.reason,
        aiGenerateDisabled,
        answerTargetMessage,
        hasPendingApprovalDraft,
        isAiGenerateQuotaExhausted,
        isAiGenerating,
        isEditing,
        isThreadEditingLocked,
        job,
        review.platform,
        targetHasAnswer,
    ]);

    const canSubmitMessage = useMemo(() => {
        if (submitDisabled) return false;
        return !!messageText.trim() || !!editedMessageText.trim();
    }, [submitDisabled, messageText, editedMessageText]);

    const submitDisabledReason = useMemo(() => {
        if (canSubmitMessage) {
            return null;
        }

        if (!answerTargetMessage) {
            return CHAT_SEND_NO_TARGET_REASON;
        }

        if (!featureControls.allowResponseSending) {
            return CHAT_SEND_FEATURE_DISABLED_REASON;
        }

        if (job) {
            return CHAT_ACTIVE_JOB_REASON;
        }

        if (isThreadEditingLocked) {
            return CHAT_THREAD_LOCKED_REASON;
        }

        if (targetHasAnswer && !isEditing && !hasPendingApprovalDraft && review.platform !== Marketplace.Ozon) {
            return CHAT_ALREADY_ANSWERED_REASON;
        }

        if (!messageText.trim() && !editedMessageText.trim()) {
            return CHAT_SEND_EMPTY_REASON;
        }

        return null;
    }, [
        answerTargetMessage,
        canSubmitMessage,
        editedMessageText,
        featureControls.allowResponseSending,
        hasPendingApprovalDraft,
        isEditing,
        isThreadEditingLocked,
        job,
        messageText,
        review.platform,
        targetHasAnswer,
    ]);

    const canShowManualEditButton = useMemo(() => {
        if (!answerTargetMessage) return false;
        if (isManualEditOpened) return false;
        if (job) return false;
        if (isThreadEditingLocked) return false;
        if (!isAiAnswerGenerated) return false;
        return !(targetHasAnswer && !isEditing && !hasPendingApprovalDraft && review.platform !== Marketplace.Ozon);
    }, [
        answerTargetMessage,
        hasPendingApprovalDraft,
        isEditing,
        isThreadEditingLocked,
        job,
        isAiAnswerGenerated,
        isManualEditOpened,
        targetHasAnswer,
        review.platform,
    ]);

    const aiGenerateLabel = useMemo(() => {
        if (isAiGenerating) return 'Генерируем...';
        return isAiAnswerGenerated ? 'Обновить ИИ-ответ' : 'ИИ-ответ';
    }, [isAiGenerating, isAiAnswerGenerated]);

    const submitButtonLabel = useMemo(() => {
        if ((isAiDraftReadyForApproval || hasPendingApprovalDraft) && !isManualEditOpened) {
            return 'Одобрить и отправить';
        }

        return 'Отправить';
    }, [hasPendingApprovalDraft, isAiDraftReadyForApproval, isManualEditOpened]);

    const onPhotoPreviewOpen = (photos: ReviewPhotoEntity[], index: number) => {
        if (!photos.length) return;

        setIsPhotoLoading(true);
        setOpenedPhotos({ photos, index });
    };

    const onPhotoPreviewClose = useCallback(() => {
        setIsPhotoLoading(false);
        setOpenedPhotos(null);
    }, []);

    const onPreviousPhoto = useCallback(() => {
        setOpenedPhotos((prevState) => {
            if (!prevState || prevState.photos.length < 2) return prevState;

            setIsPhotoLoading(true);
            const nextIndex = prevState.index === 0 ? prevState.photos.length - 1 : prevState.index - 1;

            return { ...prevState, index: nextIndex };
        });
    }, []);

    const onNextPhoto = useCallback(() => {
        setOpenedPhotos((prevState) => {
            if (!prevState || prevState.photos.length < 2) return prevState;

            setIsPhotoLoading(true);
            const nextIndex = prevState.index === prevState.photos.length - 1 ? 0 : prevState.index + 1;

            return { ...prevState, index: nextIndex };
        });
    }, []);

    const isPhotoPreviewOpen = !!openedPhotos;

    useEffect(() => {
        if (!isPhotoPreviewOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onPhotoPreviewClose();
                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onPreviousPhoto();
                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                onNextPhoto();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isPhotoPreviewOpen, onNextPhoto, onPhotoPreviewClose, onPreviousPhoto]);

    const copyMessageToEdited = useCallback(() => {
        setIsAiDraftReadyForApproval(false);
        setEditedMessageText(messageText);
    }, [messageText]);

    const openManualEdit = useCallback(() => {
        setIsAiDraftReadyForApproval(false);
        setEditedMessageText(messageText);
        setIsManualEditOpened(true);
    }, [messageText]);

    const onOpenSelectedAutoReplies = useCallback(() => {
        openSelectedAutoRepliesModal({
            type: 'reviewFeedItemIds',
            reviewFeedItemIds: [answerTargetReviewFeedItemId || review.id],
        });
    }, [answerTargetReviewFeedItemId, openSelectedAutoRepliesModal, review.id]);

    const setMessageTextWithDraftTracking = useCallback(
        (nextValue: SetStateAction<string>) => {
            const previousValue = latestMessageTextRef.current;
            const resolvedValue = typeof nextValue === 'function' ? nextValue(previousValue) : nextValue;

            if (resolvedValue === previousValue) {
                return;
            }

            if (isAiGenerating) {
                void aiAnswerJob.cancelActive('draft_changed');
            }
            applyMessageText(resolvedValue);
            if (isAiAnswerGenerated && !hasPendingApprovalDraft) {
                setIsAiDraftReadyForApproval(false);
            }
        },
        [aiAnswerJob, applyMessageText, hasPendingApprovalDraft, isAiAnswerGenerated, isAiGenerating],
    );

    const onEditedMessageTextChange = useCallback(
        (value: string) => {
            if (isAiGenerating) {
                void aiAnswerJob.cancelActive('draft_changed');
            }
            setEditedMessageText(value);
            if (isAiAnswerGenerated) {
                setIsAiDraftReadyForApproval(false);
            }
        },
        [aiAnswerJob, isAiAnswerGenerated, isAiGenerating],
    );

    const onOpenedPhotoResolved = useCallback(() => {
        setIsPhotoLoading(false);
    }, []);

    const templatesBindings = useModalAnswerPickedTemplates({
        isShown: true,
        accessToken,
        value: messageText,
        setValue: setMessageTextWithDraftTracking,
        onTemplateApplied: (templateText) => {
            void aiAnswerJob.cancelActive('template_applied');
            setAnswerDraftSource(createTemplateAnswerDraftSource(templateText));
            setIsAiAnswerGenerated(false);
            setIsAiDraftReadyForApproval(false);
        },
    });

    const openedPhoto = openedPhotos?.photos[openedPhotos.index];

    return {
        review,
        history,
        answerTargetReviewFeedItemId,
        answerTargetKey,
        hasAnswerJob,
        isThreadEditingLocked,
        messageText,
        setMessageText: (value) => setMessageTextWithDraftTracking(value),
        editedMessageText,
        setEditedMessageText: onEditedMessageTextChange,
        aiGenerateLabel,
        aiGenerateDisabledReason,
        submitDisabledReason,
        isAiGenerateQuotaExhausted,
        submitButtonLabel,
        isManualEditOpened,
        aiErrorText,
        isEditing,
        isAiGenerating,
        textAreaDisabled,
        aiGenerateDisabled,
        canShowManualEditButton,
        canSubmitMessage,
        changeEditing,
        onAnswerTargetSelect,
        isAnswerTargetSelectable,
        submitSendChatMessage,
        generateAiChatMessage,
        onOpenSelectedAutoReplies,
        openManualEdit,
        copyMessageToEdited,
        openedPhotos,
        openedPhoto,
        isPhotoLoading,
        onPhotoPreviewOpen,
        onPhotoPreviewClose,
        onPreviousPhoto,
        onNextPhoto,
        onOpenedPhotoResolved,
        ...templatesBindings,
    };
};
